<template>
  <v-dialog :model-value="modelValue" max-width="720" @update:modelValue="close">
    <div class="modal">
      <header class="modal-header">
        <div>
          <div class="form-eyebrow">Project</div>
          <h2 class="form-title">{{ project?.displayName || project?.projectName }}</h2>
          <p class="form-subtitle">
            {{ project?.roboflowProjectId }}<span v-if="project?.projectType"> · {{ project.projectType }}</span>
          </p>
        </div>
      </header>

      <!-- Inference setup: workflow + active -->
      <section class="setup-card">
        <div class="setup-eyebrow">Inference setup</div>

        <div class="setup-row">
          <div class="setup-label">
            <div class="label-title">Run workflow</div>
            <div class="label-sub">The Roboflow workflow Duallo runs — this is the inference endpoint.</div>
          </div>
          <div class="setup-control">
            <template v-if="readonly">
              <span class="mono">{{ project?.baseWorkflowUrl || '—' }}</span>
            </template>
            <template v-else>
              <v-select :model-value="selectedWorkflowUrl" :items="workflows" item-title="name" item-value="url"
                variant="outlined" density="compact" hide-details placeholder="Select a workflow…"
                class="wf-field" :loading="loadingWorkflows" :disabled="savingWorkflow"
                :no-data-text="loadingWorkflows ? 'Loading…' : 'No associated workflows'"
                @update:modelValue="onWorkflowChange" />
              <span class="wf-state">
                <v-progress-circular v-if="savingWorkflow" indeterminate size="14" width="2" color="#64ffda" />
                <v-icon v-else-if="workflowSaved" size="16" color="#64ffda">mdi-check</v-icon>
              </span>
            </template>
          </div>
        </div>

        <div class="setup-divider"></div>

        <div class="setup-row">
          <div class="setup-label">
            <div class="label-title">Active</div>
            <div class="label-sub">When on, this project runs inference on new scans.</div>
          </div>
          <div class="setup-control">
            <template v-if="readonly">
              <span class="mono">{{ project?.isActive ? 'On' : 'Off' }}</span>
            </template>
            <template v-else>
              <v-switch v-if="selectedWorkflowUrl" :model-value="project?.isActive" color="#64ffda"
                density="compact" hide-details inset :disabled="savingActive" :loading="savingActive"
                @update:modelValue="onActiveChange" />
              <span v-else class="needs-workflow">Select a workflow first</span>
            </template>
          </div>
        </div>
      </section>

      <!-- Classifications -->
      <section class="classes-block">
        <div class="section-head">
          <div class="sub-title">Classifications</div>
          <p class="section-desc">Classes are managed in Roboflow. Choose how Duallo handles each one:</p>
        </div>

        <div class="mode-legend">
          <span class="legend-item"><span class="dot off"></span>Off — ignored entirely</span>
          <span class="legend-item"><span class="dot training"></span>Training — captures evidence for review, no anomalies</span>
          <span class="legend-item"><span class="dot live"></span>Live — creates anomalies users can see</span>
        </div>

        <div v-if="loadError" class="inline-error">
          <v-icon size="18" color="#ff8a80">mdi-alert-circle-outline</v-icon>
          <span>Couldn't load classifications. Try again.</span>
        </div>

        <div class="class-list">
          <div v-if="isLoading" class="muted small">Loading…</div>
          <div v-else-if="classes.length === 0" class="muted small">
            No classes found for this project in Roboflow.
          </div>

          <div v-for="cls in classes" :key="cls.className" class="class-row">
            <div class="class-info">
              <span class="class-name">{{ cls.className }}</span>
              <span v-if="!cls.isConfigured" class="badge-default">not set</span>
            </div>
            <div class="class-controls">
              <div class="segmented">
                <button v-for="opt in MODES" :key="opt" type="button" class="seg-btn"
                  :class="[`seg-${opt.toLowerCase()}`, { 'is-active': cls.mode === opt }]"
                  :disabled="readonly || savingKeys.has(normalized(cls.className))" @click="setMode(cls, opt)">
                  {{ opt }}
                </button>
              </div>
              <div class="conf">
                <label class="conf-label">min conf</label>
                <input class="conf-input" type="number" min="0" max="1" step="0.05"
                  :value="cls.minConfidence ?? 0" :disabled="readonly || savingKeys.has(normalized(cls.className))"
                  @change="setConfidence(cls, $event.target.value)" />
              </div>
              <span class="row-state">
                <v-progress-circular v-if="savingKeys.has(normalized(cls.className))" indeterminate size="14" width="2" color="#64ffda" />
                <v-icon v-else-if="savedKeys.has(normalized(cls.className))" size="16" color="#64ffda">mdi-check</v-icon>
                <v-icon v-else-if="errorKeys.has(normalized(cls.className))" size="16" color="#ff8a80">mdi-alert-circle-outline</v-icon>
              </span>
            </div>
          </div>
        </div>
      </section>

      <div class="modal-actions">
        <button type="button" class="text-btn" @click="close">Close</button>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch } from 'vue';
import AiProjectService from '@/services/AiProjectService';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  project: { type: Object, default: null },
  readonly: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'updated']);

const MODES = ['Off', 'Training', 'Live'];

const classes = ref([]);
const isLoading = ref(false);
const loadError = ref(false);

const savingKeys = ref(new Set());
const savedKeys = ref(new Set());
const errorKeys = ref(new Set());

const normalized = (name) => (name ?? '').trim().toLowerCase();
const mark = (set, key, on) => {
  const next = new Set(set.value);
  if (on) next.add(key); else next.delete(key);
  set.value = next;
};

watch(() => props.modelValue, (open) => {
  if (open && props.project) {
    selectedWorkflowUrl.value = props.project.baseWorkflowUrl || '';
    loadClasses();
    loadWorkflows();
  }
});

// --- Run workflow (the inference endpoint) ---
const workflows = ref([]);
const loadingWorkflows = ref(false);
const selectedWorkflowUrl = ref('');
const savingWorkflow = ref(false);
const workflowSaved = ref(false);

const loadWorkflows = async () => {
  workflows.value = [];
  loadingWorkflows.value = true;
  try {
    workflows.value = await AiProjectService.getProjectWorkflows(props.project.id);
    // Keep the stored selection if it's still valid; otherwise leave it unselected so the admin
    // makes a deliberate choice (no auto-default — a project shouldn't silently gain a workflow).
    if (!workflows.value.some((w) => w.url === selectedWorkflowUrl.value)) {
      selectedWorkflowUrl.value = '';
    }
  } catch {
    workflows.value = [];
  } finally {
    loadingWorkflows.value = false;
  }
};

const onWorkflowChange = async (url) => {
  const wf = workflows.value.find((w) => w.url === url);
  if (!wf) return;
  selectedWorkflowUrl.value = url;
  savingWorkflow.value = true;
  workflowSaved.value = false;
  try {
    // Save the workflow only — activation is a separate, deliberate action by the admin.
    const updated = await AiProjectService.updateProject(props.project.id, {
      displayName: props.project.displayName,
      description: props.project.description,
      isActive: props.project.isActive,
      baseWorkflowUrl: wf.url,
      baseWorkflowId: wf.id,
    });
    // Let the parent update its copy (avoid mutating the prop directly).
    if (updated) emit('updated', updated);
    workflowSaved.value = true;
  } finally {
    savingWorkflow.value = false;
  }
};

const savingActive = ref(false);
const onActiveChange = async (value) => {
  if (!selectedWorkflowUrl.value) return;
  savingActive.value = true;
  try {
    // Workflow is already persisted; toggle active on its own (Update keeps the stored workflow).
    const updated = await AiProjectService.updateProject(props.project.id, {
      displayName: props.project.displayName,
      description: props.project.description,
      isActive: value,
    });
    if (updated) emit('updated', updated);
  } finally {
    savingActive.value = false;
  }
};

const loadClasses = async () => {
  isLoading.value = true;
  loadError.value = false;
  savedKeys.value = new Set();
  errorKeys.value = new Set();
  try {
    classes.value = await AiProjectService.getProjectClasses(props.project.id);
  } catch {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const save = async (cls, changes) => {
  const key = normalized(cls.className);
  mark(savingKeys, key, true);
  mark(savedKeys, key, false);
  mark(errorKeys, key, false);
  try {
    const saved = await AiProjectService.upsertProjectClass(props.project.id, {
      className: cls.className,
      mode: changes.mode ?? cls.mode,
      minConfidence: changes.minConfidence ?? cls.minConfidence ?? 0,
    });
    Object.assign(cls, {
      mode: saved?.mode ?? changes.mode ?? cls.mode,
      minConfidence: saved?.minConfidence ?? changes.minConfidence ?? cls.minConfidence ?? 0,
      isConfigured: true,
    });
    mark(savedKeys, key, true);
  } catch {
    mark(errorKeys, key, true);
  } finally {
    mark(savingKeys, key, false);
  }
};

const setMode = (cls, mode) => { if (cls.mode !== mode) save(cls, { mode }); };
const setConfidence = (cls, raw) => {
  let value = Number.parseFloat(raw);
  if (Number.isNaN(value)) value = 0;
  value = Math.min(1, Math.max(0, value));
  save(cls, { minConfidence: value });
};

const close = () => emit('update:modelValue', false);
</script>

<style scoped>
.modal {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px; padding: 22px; color: #fff;
  display: flex; flex-direction: column; gap: 16px;
  user-select: none;
}
.modal-header { display: flex; flex-direction: column; gap: 4px; }
.form-eyebrow { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8aa0aa; }
.form-title { font-size: 1.3rem; font-weight: 700; margin: 0; }
.form-subtitle { font-size: 0.82rem; color: #b0bec5; margin: 0; font-family: 'JetBrains Mono', Consolas, monospace; }

.classes-block { display: flex; flex-direction: column; gap: 10px; }
.mono { font-family: 'JetBrains Mono', Consolas, monospace; color: #cfd8dc; font-size: 0.82rem; }
.sub-title { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em; color: #8aa0aa; font-weight: 700; }
.muted { color: #8aa0aa; }
.small { font-size: 0.78rem; }

/* Inference setup card */
.setup-card {
  display: flex; flex-direction: column; gap: 14px; padding: 16px;
  border-radius: 14px; background: rgba(255, 255, 255, 0.025); border: 1px solid rgba(255, 255, 255, 0.06);
}
.setup-eyebrow { font-size: 0.66rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8aa0aa; }
.setup-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.setup-label { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.label-title { font-size: 0.92rem; font-weight: 700; color: #fff; }
.label-sub { font-size: 0.76rem; color: #8aa0aa; line-height: 1.4; max-width: 340px; }
.setup-control { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.setup-divider { height: 1px; background: rgba(255, 255, 255, 0.06); }
.wf-field { min-width: 240px; max-width: 300px; }
.wf-field :deep(.v-field) { border-radius: 10px; background: rgba(255, 255, 255, 0.03); font-size: 0.85rem; }
.wf-field :deep(input) { caret-color: transparent; }
.wf-state { width: 18px; display: inline-flex; justify-content: center; }
.needs-workflow {
  font-size: 0.68rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  color: #ffcc80; background: rgba(255, 204, 128, 0.1); border: 1px solid rgba(255, 204, 128, 0.28);
  padding: 2px 8px; border-radius: 999px; white-space: nowrap;
}

/* Classifications */
.section-head { display: flex; flex-direction: column; gap: 4px; }
.section-desc { margin: 0; font-size: 0.82rem; color: #b0bec5; }
.mode-legend {
  display: flex; flex-wrap: wrap; gap: 8px 16px; padding: 10px 12px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.05); font-size: 0.74rem; color: #9fb0b6;
}
.legend-item { display: inline-flex; align-items: center; gap: 6px; }
.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dot.off { background: #78909c; }
.dot.training { background: #ffb24d; }
.dot.live { background: var(--duallo-accent); }

.inline-error {
  display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 10px;
  background: rgba(255, 138, 128, 0.08); border: 1px solid rgba(255, 138, 128, 0.2); color: #ffb4ab; font-size: 0.85rem;
}

.class-list { display: flex; flex-direction: column; gap: 6px; max-height: 40vh; overflow-y: auto; }
.class-row {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 10px 12px; border-radius: 10px; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05);
}
.class-info { display: flex; align-items: center; gap: 8px; min-width: 0; }
.class-name { color: #fff; font-weight: 600; font-size: 0.9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.badge-default { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: #8aa0aa; border: 1px solid rgba(255,255,255,0.12); border-radius: 6px; padding: 1px 6px; }

.class-controls { display: flex; align-items: center; gap: 12px; }
.segmented { display: inline-flex; border-radius: 9px; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.1); }
.seg-btn {
  padding: 6px 12px; background: transparent; border: none; cursor: pointer; color: #9fb0b6;
  font: inherit; font-size: 0.78rem; font-weight: 600; border-right: 1px solid rgba(255,255,255,0.08);
}
.seg-btn:last-child { border-right: none; }
.seg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.seg-btn:not(:disabled):hover { background: rgba(255,255,255,0.05); color: #fff; }
.seg-btn.seg-off.is-active { background: rgba(120, 144, 156, 0.28); color: #eceff1; }
.seg-btn.seg-training.is-active { background: rgba(255, 178, 77, 0.22); color: #ffcc80; }
.seg-btn.seg-live.is-active { background: rgba(100, 255, 218, 0.2); color: var(--duallo-accent); }

.conf { display: flex; align-items: center; gap: 6px; }
.conf-label { font-size: 0.68rem; color: #8aa0aa; text-transform: uppercase; letter-spacing: 0.04em; }
.conf-input {
  width: 62px; padding: 5px 8px; border-radius: 8px; background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1); color: #fff; font: inherit; font-size: 0.82rem;
}
.row-state { width: 18px; display: inline-flex; justify-content: center; }

.modal-actions { display: flex; justify-content: flex-end; }
.text-btn {
  padding: 10px 18px; border-radius: 10px; background: transparent; border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc; font: inherit; font-weight: 600; cursor: pointer;
}
</style>
